import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import CompareArrows from "@material-ui/icons/CompareArrows";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import Search from "@material-ui/icons/Search";
import Close from "@material-ui/icons/Close";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";

import { grayColor } from "../../../../../assets/jss/material-dashboard-pro-react";
import PaneErrorBanner from "./PaneErrorBanner";
import DrawerPaneLoadingLayout from "../../DrawerPaneLoadingLayout";
import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import { saveCrossLinkOverridesV3 } from "../../../../../services/inputsV3Service";

// ---- Helpers --------------------------------------------------------------

// Normaliza texto para busca "tolerante" — lowercase + remove acentos.
// Espelha o mesmo helper usado pelo filtro de inputs em ``InputsV3.js``
// para consistencia de comportamento (acentos opcionais, case-insensitive).
const normalizeForSearch = (str) =>
  (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// Reads warn/crit thresholds from the cross-link descriptor.
// Returns percentages (0-100). Defaults critPct to 100 when absent.
const extractRatios = (threshold) => {
  if (!threshold || typeof threshold !== "object") {
    return { warnPct: null, critPct: 100 };
  }
  const warn = threshold.max_ratio;
  const crit = threshold.within_tolerance_ratio;
  return {
    warnPct: warn !== undefined && warn !== null ? warn * 100 : null,
    critPct: crit !== undefined && crit !== null ? crit * 100 : 100,
  };
};

const formatPct = (pct, naLabel = "n/a") => {
  if (pct === null || pct === undefined) return naLabel;
  const n = Number(pct);
  if (!Number.isFinite(n)) return naLabel;
  if (Math.abs(n - 100) < 1e-6) return "100%";
  return `${n.toFixed(1).replace(".", ",")}%`;
};

const coveragePctFromMissingPct = (pct) => {
  if (pct === null || pct === undefined) return null;
  const n = Number(pct);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, 100 - n));
};

const formatCoverageLimit = (missingPct, naLabel = "n/a") => {
  const coveragePct = coveragePctFromMissingPct(missingPct);
  return formatPct(coveragePct, naLabel);
};

// Resolves a human-readable input name with priority: input_name dict → display_name → meta_name.
const readableInputName = (input, fallback) => {
  const fb = fallback || "Este input";
  if (!input) return fb;
  if (input.input_name && typeof input.input_name === "object") {
    const lang =
      (typeof navigator !== "undefined" &&
        (navigator.language || "").split("-")[0]) ||
      "pt";
    return (
      input.input_name[lang] ||
      input.input_name.pt ||
      input.input_name.es ||
      input.input_name.en ||
      input.display_name ||
      input.meta_name ||
      fb
    );
  }
  return input.display_name || input.meta_name || fb;
};

// Slider step: 0.5% granularity.
const SLIDER_STEP = 0.5;
const MONTHS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const snapToStep = (pct) => Math.round(pct / SLIDER_STEP) * SLIDER_STEP;
const periodKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

// Ordena os participantes de um cruzamento na ordem "natural" para
// leitura visual da cadeia.
//
// Regra (rev. 2026-05-31 — substitui o "input aberto sempre primeiro"
// de 2026-05-27):
//   - Endpoints (fact / aggregate / dimension) ficam nas PONTAS;
//     bridges (intermediarios / de-para) ficam SEMPRE no MEIO — mesmo
//     quando o proprio input aberto no drawer e um bridge. Antes,
//     fixar o input aberto na esquerda fazia um intermediario parecer
//     endpoint (ex.: abrir "Maestro Nodos" exibia
//     "Maestro Nodos ↔ Movimientos ↔ Tarifario" em vez do natural
//     "Movimientos ↔ Maestro Nodos ↔ Tarifario").
//   - O input aberto continua sendo a ANCORA a esquerda QUANDO e um
//     endpoint (preserva a intencao original). Quando e bridge, fica
//     no meio com os demais bridges.
//   - Desempate deterministico por nome normalizado (a lista nao
//     "muda toda vez").
//   - Ordenar so por ROLE_ORDER nao bastaria: em bridge_coverage o
//     source padrao e ``aggregate`` (sem ``fact``), e o bridge cairia
//     na ponta. Por isso particionamos endpoints x bridges
//     explicitamente.
const ROLE_ORDER = { fact: 0, bridge: 1, aggregate: 2, dimension: 3 };

const orderParticipants = (
  xl,
  thisInputMeta,
  thisInputDisplayName,
  fallback,
) => {
  const thisRole = Array.isArray(xl.role) ? xl.role[0] : xl.role;
  const thisNode = {
    meta: thisInputMeta || null,
    role: thisRole,
    label: thisInputDisplayName || fallback,
    isThis: true,
  };
  const others = (xl.other_participants || []).map((p) => ({
    meta: p.input_meta_name || null,
    role: p.role,
    label: p.display_name || p.input_meta_name,
    isThis: false,
  }));
  const all = [thisNode, ...others];

  const byRoleThenName = (a, b) => {
    const ra = ROLE_ORDER[a.role] ?? 99;
    const rb = ROLE_ORDER[b.role] ?? 99;
    if (ra !== rb) return ra - rb;
    const na = normalizeForSearch(a.label);
    const nb = normalizeForSearch(b.label);
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  };

  const endpoints = all.filter((p) => p.role !== "bridge").sort(byRoleThenName);
  const bridges = all.filter((p) => p.role === "bridge").sort(byRoleThenName);

  // Ancora a esquerda: se o input aberto e endpoint, traz para a frente.
  if (thisNode.role !== "bridge") {
    const idx = endpoints.findIndex((p) => p.isThis);
    if (idx > 0) {
      const [self] = endpoints.splice(idx, 1);
      endpoints.unshift(self);
    }
  }

  // Edge: cruzamento so de bridges (atipico) — devolve como esta.
  if (endpoints.length === 0) return bridges;
  return [endpoints[0], ...bridges, ...endpoints.slice(1)];
};

// Builds the visual chain do cruzamento. ``isThis`` marca o no do
// input aberto (sublinhado na UI). ``isEndpoint`` controla o peso:
// endpoints em NEGRITO, bridges em peso REGULAR.
const buildChain = (xl, thisInputMeta, thisInputDisplayName, fallback) =>
  orderParticipants(xl, thisInputMeta, thisInputDisplayName, fallback).map(
    (p) => ({
      label: p.label,
      isEndpoint: p.role !== "bridge",
      isThis: p.isThis,
    }),
  );

// Formats an active_from/active_to bound ({year, month}) as "mmm/YY".
// Mes abreviado via ``Intl.DateTimeFormat`` para respeitar o idioma
// ativo sem precisarmos manter listas hard-coded por locale.
const formatPeriodBound = (bound, locale) => {
  if (!bound || typeof bound !== "object") return null;
  const y = Number(bound.year);
  const m = Number(bound.month);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
    return null;
  }
  const lang = (locale || "pt").slice(0, 2);
  // ``Date`` 0-indexed para o mes; usamos dia 1 para evitar overflow
  // em meses curtos. ``Intl`` devolve "fev." em pt-BR — strip do ponto
  // final para casar com o estilo "mmm/YY".
  const abbr = new Intl.DateTimeFormat(lang, { month: "short" })
    .format(new Date(y, m - 1, 1))
    .replace(/\.$/, "")
    .toLowerCase();
  return `${abbr}/${String(y).slice(-2)}`;
};

// Builds the "Vigente de X a Y" tag from xl.effective_period.
// Returns null when there's no temporal constraint (all participants
// are permanent). Tag mirrors the visual treatment of the "Cruza por"
// subtitle to keep the card layout consistent.
const buildEffectivePeriod = (xl, t, locale) => {
  const ep = xl?.effective_period;
  if (!ep || typeof ep !== "object") return null;
  const fromTxt = formatPeriodBound(ep.active_from, locale);
  const toTxt = formatPeriodBound(ep.active_to, locale);
  if (!fromTxt && !toTxt) return null;
  if (fromTxt && toTxt) {
    return t("modeling_inputs_v3.cross_links.effective_period_range", {
      from: fromTxt,
      to: toTxt,
    });
  }
  if (fromTxt) {
    return t("modeling_inputs_v3.cross_links.effective_period_from", {
      from: fromTxt,
    });
  }
  return t("modeling_inputs_v3.cross_links.effective_period_until", {
    to: toTxt,
  });
};

// Builds the subtitle line from declared columns.
const buildSubtitle = (xl, t) => {
  const keys = (xl.key_columns || []).filter(Boolean);
  const measure = xl.measure_column;
  const parts = [];
  if (keys.length) {
    parts.push(
      <>
        {t("modeling_inputs_v3.cross_links.subtitle_crosses_by")}{" "}
        <strong>{keys.join(", ")}</strong>
      </>,
    );
  }
  if (measure) {
    parts.push(
      <>
        {t("modeling_inputs_v3.cross_links.subtitle_weighted_by")}{" "}
        <strong>{measure}</strong>
      </>,
    );
  }
  if (!parts.length) return null;
  return (
    <>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 ? "; " : null}
          {p}
        </React.Fragment>
      ))}
      .
    </>
  );
};

// ---- Threshold slider -----------------------------------------------------
// Three solid color zones based on % unmatched: green [0..warn], yellow [warn..crit], red [crit..100].
// Labels show % matched (inverted) for user-facing clarity.
const ThresholdSlider = ({ classes, warnPct, critPct, onChange, t }) => {
  const wp = Math.max(0, Math.min(100, warnPct ?? 0));
  const cp =
    critPct !== null && critPct !== undefined
      ? Math.max(wp, Math.min(100, critPct))
      : null;

  const yellowEnd = cp !== null ? cp : 100;
  const yellowWidth = Math.max(0, yellowEnd - wp);
  const redWidth = cp !== null ? Math.max(0, 100 - cp) : 0;

  const railRef = useRef(null);
  const [dragging, setDragging] = useState(null);

  const handleDown = (which) => (e) => {
    if (!onChange) return;
    e.preventDefault();
    setDragging(which);
  };

  useEffect(() => {
    if (!dragging || !onChange) return undefined;
    const move = (ev) => {
      const rail = railRef.current;
      if (!rail) return;
      const rect = rail.getBoundingClientRect();
      const clientX = ev.clientX ?? ev.touches?.[0]?.clientX;
      if (clientX === undefined) return;
      const raw = ((clientX - rect.left) / rect.width) * 100;
      const pct = snapToStep(Math.max(0, Math.min(100, raw)));
      if (dragging === "warn") {
        const next = cp !== null ? Math.min(pct, cp) : pct;
        onChange({ warnPct: next, critPct });
      } else {
        const next = Math.max(pct, wp);
        onChange({ warnPct, critPct: next });
      }
    };
    const up = () => setDragging(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, onChange, warnPct, critPct, wp, cp]);

  const thumbCls = onChange
    ? `${classes.xlinkSliderThumb} ${classes.xlinkSliderThumbInteractive}`
    : classes.xlinkSliderThumb;

  // Onda 3.4 (CCU Testes, mai/2026): label combinado ("opcao C") quando
  // os thumbs ficam proximos demais. Em vez de dois labels separados
  // que se sobrepoem, exibimos UM label centrado entre os dois pontos
  // com formato "atencao / critico" (ex.: "72,0% / 75,0%"). O delta
  // ``COLLISION_PCT=12`` foi calibrado para "XX,X%" caber sem
  // sobreposicao em fontes ate ~12px. Os labels individuais somem
  // por opacity (transicao suave) — UI mantida estavel durante o
  // drag.
  const COLLISION_PCT = 12;
  const labelsCollide = cp !== null && Math.abs(cp - wp) < COLLISION_PCT;
  const combinedLeftPct = cp !== null ? (wp + cp) / 2 : wp;

  return (
    <div className={classes.xlinkSliderBlock}>
      <div className={classes.xlinkSliderRail} ref={railRef}>
        <div className={classes.xlinkSliderTrack}>
          <div
            className={`${classes.xlinkSliderZone} ${classes.xlinkSliderZoneGreen}`}
            style={{ left: 0, width: `${wp}%` }}
          />
          <div
            className={`${classes.xlinkSliderZone} ${classes.xlinkSliderZoneYellow}`}
            style={{ left: `${wp}%`, width: `${yellowWidth}%` }}
          />
          {cp !== null ? (
            <div
              className={`${classes.xlinkSliderZone} ${classes.xlinkSliderZoneRed}`}
              style={{ left: `${cp}%`, width: `${redWidth}%` }}
            />
          ) : null}
        </div>
        <div className={classes.xlinkSliderThumbs}>
          <div
            className={thumbCls}
            style={{ left: `${wp}%` }}
            onMouseDown={handleDown("warn")}
            onTouchStart={handleDown("warn")}
            role={onChange ? "slider" : undefined}
            aria-label={
              onChange
                ? t("modeling_inputs_v3.cross_links.warn_threshold")
                : undefined
            }
            aria-valuemin={0}
            aria-valuemax={cp !== null ? cp : 100}
            aria-valuenow={wp}
          >
            <div
              className={classes.xlinkSliderThumbLabel}
              style={{
                left: 8,
                opacity: labelsCollide ? 0 : 1,
                transition: "opacity 120ms ease",
              }}
            >
              {formatCoverageLimit(
                warnPct,
                t("modeling_inputs_v3.crosslinks_pane.not_applicable"),
              )}
            </div>
          </div>
          {cp !== null ? (
            <div
              className={thumbCls}
              style={{ left: `${cp}%` }}
              onMouseDown={handleDown("crit")}
              onTouchStart={handleDown("crit")}
              role={onChange ? "slider" : undefined}
              aria-label={
                onChange
                  ? t("modeling_inputs_v3.cross_links.crit_threshold")
                  : undefined
              }
              aria-valuemin={wp}
              aria-valuemax={100}
              aria-valuenow={cp}
            >
              <div
                className={classes.xlinkSliderThumbLabel}
                style={{
                  left: 8,
                  opacity: labelsCollide ? 0 : 1,
                  transition: "opacity 120ms ease",
                }}
              >
                {formatCoverageLimit(
                  critPct,
                  t("modeling_inputs_v3.crosslinks_pane.not_applicable"),
                )}
              </div>
            </div>
          ) : null}
          {/* Label combinado: aparece no centro entre os thumbs quando
              colidem. Renderizamos um "thumb fantasma" com EXATAMENTE
              as mesmas dimensoes/posicionamento dos thumbs reais
              (``xlinkSliderThumb``) — width/height/top/transform — para
              que o label interno (``xlinkSliderThumbLabel`` com
              ``top: -24``) caia na MESMA altura dos labels
              individualizados. Apenas o background fica transparente
              (sem bolinha branca) e o pointer-events: none evita
              interferir no drag. */}
          {labelsCollide && cp !== null ? (
            <div
              className={classes.xlinkSliderThumb}
              style={{
                left: `${combinedLeftPct}%`,
                background: "transparent",
                boxShadow: "none",
                pointerEvents: "none",
              }}
            >
              <div
                className={classes.xlinkSliderThumbLabel}
                style={{
                  left: 8,
                  whiteSpace: "nowrap",
                  opacity: 1,
                  transition: "opacity 120ms ease",
                }}
              >
                {formatCoverageLimit(
                  warnPct,
                  t("modeling_inputs_v3.crosslinks_pane.not_applicable"),
                )}{" "}
                |{" "}
                {formatCoverageLimit(
                  critPct,
                  t("modeling_inputs_v3.crosslinks_pane.not_applicable"),
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className={classes.xlinkSliderScale}>
        <span>100%</span>
        <span>75%</span>
        <span>50%</span>
        <span>25%</span>
        <span>0%</span>
      </div>
    </div>
  );
};

ThresholdSlider.propTypes = {
  classes: PropTypes.object.isRequired,
  warnPct: PropTypes.number,
  critPct: PropTypes.number,
  onChange: PropTypes.func,
  t: PropTypes.func.isRequired,
};

// ---- Inline editor --------------------------------------------------------

const ThresholdEditor = ({
  classes,
  draft,
  onDraftChange,
  onCancel,
  onConfirm,
  changed,
  saving,
  errorMsg,
  xl,
  thisInputMeta,
  thisInputDisplayName,
  t,
}) => {
  const {
    warnPct,
    critPct,
    retroactive,
    onCriticalSeverity,
    authoritativeMetaNames = [],
  } = draft;

  const setSlider = ({ warnPct: w, critPct: c }) => {
    onDraftChange({ ...draft, warnPct: w, critPct: c });
  };

  const toggleRetroactive = (e) => {
    onDraftChange({ ...draft, retroactive: e.target.checked });
  };

  const setOnCritical = (e) => {
    onDraftChange({ ...draft, onCriticalSeverity: e.target.value });
  };

  // Onda 3.4 (CCU Testes, mai/2026): regra de negocio "no maximo um
  // mestre por cruzamento" — converte o conjunto em uma escolha unica
  // (radio + opcao "Nenhum"). Antes era checkbox multiplo, o que
  // permitia marcar varios participantes simultaneamente; isso nao
  // fazia sentido com a UX de "sugerido = rejeitar o NAO-mestre"
  // (multiplos mestres tornavam a sugestao ambigua).
  const setAuthoritative = (meta) => () => {
    onDraftChange({
      ...draft,
      authoritativeMetaNames: meta ? [meta] : [],
    });
  };
  const currentAuthoritative = authoritativeMetaNames[0] || null;

  // Onda 3.4 (CCU Testes, mai/2026): "Falhar direto" so faz sentido
  // quando o cruzamento envolve EXATAMENTE 2 inputs E um deles e o
  // mestre (fonte da verdade). Para 3+ inputs ou sem mestre, nao ha
  // como o sistema escolher quem rejeitar — restara' sempre perguntar.
  const totalParticipants = 1 + (xl?.other_participants?.length || 0);
  const hasAuthoritative = currentAuthoritative !== null;
  const onCritEnabled = totalParticipants === 2 && hasAuthoritative;
  // Render forca "ask" quando o caminho "fail" nao e' aplicavel,
  // independente do que esta no draft. O sync para o draft acontece
  // no confirm (logica em ``confirmDraft`` no caller).
  const effectiveOnCritical = onCritEnabled ? onCriticalSeverity : "ask";

  // Mesma ordenacao "natural" da cadeia (orderParticipants) para que a
  // lista de "fonte da verdade" siga a mesma sequencia visual dos nos.
  const participantRows = orderParticipants(
    xl || {},
    thisInputMeta,
    thisInputDisplayName || thisInputMeta,
    thisInputMeta,
  )
    .filter((p) => p.meta)
    .map((p) => ({ meta: p.meta, label: p.label, isThis: p.isThis }));

  // Onda 3.4 (CCU Testes, mai/2026): estilo unificado para os tres
  // titulos de secao do editor ("Limites de divergencia",
  // "Comportamento ao romper o limite critico", "Input fonte da
  // verdade") — bold + cinza escuro, sem uppercase. Substitui a
  // classe legada ``xlinkEditorTitle`` que vinha em uppercase e
  // destoava dos demais.
  const sectionTitleStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#1f2933",
    lineHeight: 1.5,
    marginBottom: 8,
  };

  return (
    <div className={classes.xlinkEditor}>
      <div style={sectionTitleStyle}>
        {t("modeling_inputs_v3.cross_links.editor_title")}
      </div>
      <div className={classes.xlinkEditorHelp}>
        {t("modeling_inputs_v3.cross_links.editor_help")}
      </div>

      <ThresholdSlider
        classes={classes}
        warnPct={warnPct}
        critPct={critPct}
        onChange={setSlider}
        t={t}
      />

      {/* Onda 3.4: "Aplicar de forma retroativa" pertence
          conceitualmente a SECAO de limites (afeta apenas a
          reclassificacao dos meses ja rodados em relacao ao slider
          acima — nao a mestre nem a comportamento critico). Movido
          para logo abaixo do slider. */}
      <div style={{ marginTop: 12 }}>
        <label className={classes.xlinkRetroactive}>
          <input
            type="checkbox"
            checked={retroactive}
            onChange={toggleRetroactive}
          />
          <span>{t("modeling_inputs_v3.cross_links.apply_retroactive")}</span>
        </label>
      </div>
      {retroactive ? (
        <div className={classes.xlinkRetroactiveImpact}>
          {t("modeling_inputs_v3.cross_links.retroactive_impact")}
        </div>
      ) : null}

      <div style={{ ...sectionTitleStyle, marginTop: 18 }}>
        {t("modeling_inputs_v3.cross_links.authoritative_title")}
      </div>
      <div
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
        role="radiogroup"
        aria-label={t("modeling_inputs_v3.cross_links.authoritative_title")}
      >
        <label className={classes.xlinkRetroactive} style={{ marginBottom: 0 }}>
          <input
            type="radio"
            name={`authoritative-${xl?.meta_name || "x"}`}
            checked={currentAuthoritative === null}
            onChange={setAuthoritative(null)}
          />
          <span>{t("modeling_inputs_v3.cross_links.authoritative_none")}</span>
        </label>
        {participantRows.map((row) => (
          <label
            key={row.meta}
            className={classes.xlinkRetroactive}
            style={{ marginBottom: 0 }}
          >
            <input
              type="radio"
              name={`authoritative-${xl?.meta_name || "x"}`}
              checked={currentAuthoritative === row.meta}
              onChange={setAuthoritative(row.meta)}
            />
            <span>
              {row.label}
              {row.isThis
                ? ` ${t("modeling_inputs_v3.cross_links.this_input")}`
                : null}
            </span>
          </label>
        ))}
      </div>

      {/* Onda 3.4 (CCU Testes, mai/2026): "Comportamento ao romper o
          limite critico" foi reposicionado AQUI (depois de mestre)
          porque depende dele — sem mestre identificado e/ou com 3+
          inputs, "falhar direto" perde sentido (o sistema nao saberia
          qual input rejeitar, e perguntar e' a unica opcao real).
          Quando inativo, o titulo carrega o complemento explicativo e
          os radios ficam desabilitados, mas seguem mostrando "ask"
          como escolha vigente. */}
      <div style={{ ...sectionTitleStyle, marginTop: 18 }}>
        {t("modeling_inputs_v3.cross_links.on_critical_title")}
        {!onCritEnabled ? (
          <span
            style={{
              fontWeight: 400,
              color: "#6b7280",
              marginLeft: 6,
              fontSize: 11,
            }}
          >
            — {t("modeling_inputs_v3.cross_links.on_critical_disabled_hint")}
          </span>
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          opacity: onCritEnabled ? 1 : 0.75,
        }}
      >
        <label className={classes.xlinkRetroactive} style={{ marginBottom: 0 }}>
          <input
            type="radio"
            name={`on-critical-${xl?.meta_name || "x"}`}
            value="ask"
            checked={effectiveOnCritical !== "fail"}
            onChange={setOnCritical}
            disabled={!onCritEnabled}
          />
          <span>{t("modeling_inputs_v3.cross_links.on_crit_ask")}</span>
        </label>
        <label className={classes.xlinkRetroactive} style={{ marginBottom: 0 }}>
          <input
            type="radio"
            name={`on-critical-${xl?.meta_name || "x"}`}
            value="fail"
            checked={effectiveOnCritical === "fail"}
            onChange={setOnCritical}
            disabled={!onCritEnabled}
          />
          <span>{t("modeling_inputs_v3.cross_links.on_crit_fail")}</span>
        </label>
      </div>

      {errorMsg ? (
        <div
          className={classes.xlinkRetroactiveImpact}
          style={{ color: "#a02e2e" }}
        >
          {errorMsg}
        </div>
      ) : null}
      {/* Onda 3.4 (CCU Testes, mai/2026): footer com borda no topo +
          margem grande para deixar visualmente claro que os botoes
          aplicam o conjunto de TODAS as decisoes acima (limites,
          mestre, comportamento critico, retroativo) — nao so a ultima
          delas. Estilos inline (sem mexer na sheet) porque so este
          ponto do CrossLinksPane precisa do tratamento. */}
      <div
        className={classes.xlinkEditorActions}
        style={{
          marginTop: 22,
          paddingTop: 14,
          borderTop: "1px solid #e0e0e0",
          justifyContent: "flex-end",
        }}
      >
        <div className={classes.xlinkEditorActionsButtons}>
          <button
            type="button"
            className={`${classes.xlinkBtn} ${classes.xlinkBtnGhost}`}
            onClick={onCancel}
          >
            {t("modeling_inputs_v3.cross_links.cancel")}
          </button>
          <button
            type="button"
            className={`${classes.xlinkBtn} ${classes.xlinkBtnPrimary}`}
            onClick={onConfirm}
            disabled={!changed || saving}
            title={
              saving
                ? t("modeling_inputs_v3.cross_links.saving")
                : !changed
                  ? t("modeling_inputs_v3.cross_links.editor_title")
                  : undefined
            }
          >
            {saving
              ? t("modeling_inputs_v3.cross_links.saving")
              : t("modeling_inputs_v3.cross_links.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

ThresholdEditor.propTypes = {
  classes: PropTypes.object.isRequired,
  draft: PropTypes.object.isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  changed: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
  errorMsg: PropTypes.string,
  xl: PropTypes.object,
  thisInputMeta: PropTypes.string,
  thisInputDisplayName: PropTypes.string,
  t: PropTypes.func.isRequired,
};

const evaluationText = (evaluation, t) => {
  if (!evaluation?.evaluated)
    return t("modeling_inputs_v3.cross_links.not_evaluated");
  const pct = evaluation.coverage_pct;
  if (pct === null || pct === undefined || Number.isNaN(Number(pct))) {
    return t("modeling_inputs_v3.cross_links.evaluated_no_pct");
  }
  return t("modeling_inputs_v3.cross_links.evaluated", {
    pct: formatPct(
      Number(pct),
      t("modeling_inputs_v3.crosslinks_pane.not_applicable"),
    ),
  });
};

// Bolinha indicadora "onde cai a avaliacao deste mes". Mapeia o
// ``state`` do ``CrossLinkControlV3`` (vindo em ``evaluation.state``)
// para um tom: success=verde, warning=amarelo, error/pending_decision=
// vermelho, demais=neutro (cinza/sem bolinha). Quando o cruzamento
// nao foi avaliado neste mes (``evaluated=false``) a bolinha some.
const evaluationTone = (evaluation) => {
  if (!evaluation?.evaluated) return null;
  const state = evaluation.state;
  if (state === "success") return "success";
  if (state === "warning") return "warning";
  if (state === "error" || state === "pending_decision") return "danger";
  return null;
};

const CoverageHistoryTable = ({
  classes,
  history,
  currentYear,
  currentMonth,
  t,
}) => {
  const values = new Map(
    (Array.isArray(history) ? history : [])
      .filter(
        (item) =>
          item?.coverage_pct !== null && item?.coverage_pct !== undefined,
      )
      .map((item) => [periodKey(item.year, item.month), item]),
  );
  const years = Array.from(
    new Set(Array.from(values.values()).map((item) => item.year)),
  ).sort((a, b) => a - b);

  if (!years.length) {
    return (
      <div className={classes.xlinkHistoryEmpty}>
        {t("modeling_inputs_v3.cross_links.no_history")}
      </div>
    );
  }

  return (
    <div className={classes.xlinkHistoryCompare}>
      <div className={classes.xlinkHistoryHead}>
        <span>⇄</span>
        <span>{t("modeling_inputs_v3.cross_links.history_title")}</span>
      </div>
      <div className={classes.xlinkHistoryGrid}>
        <div
          className={`${classes.xlinkHistoryCell} ${classes.xlinkHistoryCellHeader}`}
        />
        {MONTHS.map((m) => (
          <div
            key={`mh-${m}`}
            className={`${classes.xlinkHistoryCell} ${classes.xlinkHistoryCellHeader}`}
          >
            {m}
          </div>
        ))}
        {years.map((year) => (
          <React.Fragment key={year}>
            <div
              className={`${classes.xlinkHistoryCell} ${classes.xlinkHistoryCellYear}`}
            >
              {year}
            </div>
            {MONTHS.map((m) => {
              const month = Number(m);
              const key = periodKey(year, month);
              const item = values.get(key);
              const hasValue =
                item?.coverage_pct !== null && item?.coverage_pct !== undefined;
              const isCurrent = year === currentYear && month === currentMonth;
              return (
                <div
                  key={key}
                  className={[
                    classes.xlinkHistoryCell,
                    hasValue
                      ? classes.xlinkHistoryCellValid
                      : classes.xlinkHistoryCellOut,
                    isCurrent ? classes.xlinkHistoryCellCurrent : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {hasValue ? (
                    <span className={classes.xlinkHistoryCellValue}>
                      {formatPct(
                        Number(item.coverage_pct),
                        t("modeling_inputs_v3.crosslinks_pane.not_applicable"),
                      )}
                    </span>
                  ) : (
                    <span>—</span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

CoverageHistoryTable.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.array,
  currentYear: PropTypes.number,
  currentMonth: PropTypes.number,
  t: PropTypes.func.isRequired,
};

// ---- Main component -------------------------------------------------------

const CrossLinksPane = ({
  input,
  year,
  month,
  userRole,
  isUploading,
  drawerDetails,
}) => {
  // Only DEV/IMG/CAD can adjust thresholds.
  const canEditThresholds = ["DEV", "IMG", "CAD"].includes(userRole);
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "pt";
  const thisInputFallback = t(
    "modeling_inputs_v3.crosslinks_pane.this_input_fallback",
  );
  const naLabel = t("modeling_inputs_v3.crosslinks_pane.not_applicable");

  const thisInputDisplayName = readableInputName(input, thisInputFallback);

  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [openHistoryId, setOpenHistoryId] = useState(null);

  const [localOverrides, setLocalOverrides] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Filtro de texto: aplica-se sobre o nome do input atual + nomes dos
  // demais participantes + meta_name do cruzamento + colunas declaradas
  // (chaves/medida). Estado local — nao precisa persistir entre meses.
  const [nameFilter, setNameFilter] = useState("");
  // Token aleatorio para o ``name`` do campo de busca — evita autofill
  // do navegador/gerenciadores de senha (mesma logica do filtro do
  // ``InputsV3.js``).
  const filterFieldName = useRef(
    `crosslinks-filter-${Math.random().toString(36).slice(2, 8)}`,
  ).current;

  const crossLinks = isUploading
    ? []
    : Array.isArray(drawerDetails?.crossLinks)
      ? drawerDetails.crossLinks
      : [];
  const loading = Boolean(
    isUploading || (drawerDetails?.loading && crossLinks.length === 0),
  );
  const error = drawerDetails?.error || null;

  const openEditor = useCallback((xlMeta, baseDraft) => {
    setDrafts((d) => ({
      ...d,
      [xlMeta]: { ...baseDraft, retroactive: false },
    }));
    setExpandedId(xlMeta);
  }, []);

  const resolveAuthoritativeMetas = useCallback(
    (xl) => {
      if (!xl) return [];
      const list = [];
      if (xl.is_authoritative && input?.meta_name) {
        list.push(input.meta_name);
      }
      for (const p of xl.other_participants || []) {
        if (p?.is_authoritative && p.input_meta_name) {
          list.push(p.input_meta_name);
        }
      }
      return list;
    },
    [input],
  );
  const closeEditor = useCallback(() => {
    setExpandedId(null);
  }, []);
  const updateDraft = useCallback((xlMeta, next) => {
    setDrafts((d) => ({ ...d, [xlMeta]: next }));
  }, []);
  const confirmDraft = useCallback(
    async (xlMeta) => {
      const draft = drafts[xlMeta];
      if (!draft) return;
      const xl = crossLinks.find((c) => c.meta_name === xlMeta);
      const xlinkId = xl?.id;
      const attentionRatio = (draft.warnPct || 0) / 100;
      const criticalRatio =
        draft.critPct == null ? null : (draft.critPct || 0) / 100;

      if (!xlinkId) {
        setLocalOverrides((o) => ({
          ...o,
          [xlMeta]: { warnPct: draft.warnPct, critPct: draft.critPct },
        }));
        setExpandedId(null);
        setSaveError(t("modeling_inputs_v3.cross_links.save_error_no_id"));
        return;
      }

      // Onda 3.4 (CCU Testes, mai/2026): se o cruzamento nao atende
      // os pre-requisitos para "falhar direto" (>2 inputs ou sem
      // mestre), forcamos ``ask`` no payload mesmo que o draft
      // tenha ficado obsoleto com ``fail`` (ex.: usuario marcou
      // mestre, escolheu "fail" e depois desmarcou o mestre antes
      // de confirmar).
      const others = (xl?.other_participants || []).length;
      const totalParticipants = 1 + others;
      const draftAuthMetas = draft.authoritativeMetaNames || [];
      const onCritEnabled =
        totalParticipants === 2 && draftAuthMetas.length > 0;
      const effectiveOnCritical = onCritEnabled
        ? draft.onCriticalSeverity
        : "ask";

      setSavingId(xlMeta);
      setSaveError(null);
      try {
        await saveCrossLinkOverridesV3({
          xlinkId,
          attentionRatio,
          criticalRatio,
          applyRetroactively: !!draft.retroactive,
          onCriticalSeverity: effectiveOnCritical,
          authoritativeMetaNames: draftAuthMetas,
        });
        setLocalOverrides((o) => ({
          ...o,
          [xlMeta]: { warnPct: draft.warnPct, critPct: draft.critPct },
        }));
        setExpandedId(null);
        if (typeof drawerDetails?.refresh === "function") {
          await drawerDetails.refresh();
        }
      } catch (e) {
        const code = e?.response?.data?.error_code;
        const msg =
          code === "forbidden_threshold_override"
            ? t("modeling_inputs_v3.cross_links.save_error_forbidden")
            : code === "critical_must_be_above_attention"
              ? t(
                  "modeling_inputs_v3.cross_links.save_error_critical_above_attention",
                )
              : code
                ? `${t("modeling_inputs_v3.cross_links.save_error_generic")} (${code})`
                : t("modeling_inputs_v3.cross_links.save_error_generic");
        setSaveError(msg);
      } finally {
        setSavingId(null);
      }
    },
    [drafts, crossLinks, drawerDetails, t],
  );

  const enrichedList = useMemo(() => {
    const mapped = crossLinks.map((xl) => {
      const base = extractRatios(xl.threshold);
      const ovr = localOverrides[xl.meta_name];
      const persistedAttn =
        typeof xl.attention_threshold_override === "number"
          ? xl.attention_threshold_override * 100
          : null;
      const persistedCrit =
        typeof xl.critical_threshold_override === "number"
          ? xl.critical_threshold_override * 100
          : null;
      const descriptorCrit =
        base.critPct !== null && base.critPct !== undefined
          ? base.critPct
          : 100;
      return {
        xl,
        warnPct: ovr
          ? ovr.warnPct
          : persistedAttn != null
            ? persistedAttn
            : base.warnPct,
        critPct: ovr
          ? ovr.critPct
          : persistedCrit != null
            ? persistedCrit
            : descriptorCrit,
      };
    });
    // Ordenacao deterministica por uma chave VISIVEL no card: o card
    // exibe a CADEIA (buildChain), nao o ``display_name`` do xlink —
    // ordenar pelo display_name (invisivel) fazia os cards parecerem
    // "fora de ordem". Agora ordenamos pelo nome da(s) contraparte(s)
    // (os nos que NAO sao o input aberto), seguido do texto completo
    // da cadeia como desempate. Continua deterministico (resolve o
    // "muda toda vez"), mas alinhado ao que o usuario enxerga.
    const chainSortKeys = (xl) => {
      const ordered = orderParticipants(
        xl,
        input?.meta_name,
        thisInputDisplayName,
        thisInputFallback,
      );
      const counterparts = ordered
        .filter((p) => !p.isThis)
        .map((p) => p.label)
        .join(" ");
      const fullChain = ordered.map((p) => p.label).join(" ");
      return {
        primary: normalizeForSearch(counterparts),
        secondary: normalizeForSearch(fullChain),
      };
    };
    mapped.sort((a, b) => {
      const ka = chainSortKeys(a.xl);
      const kb = chainSortKeys(b.xl);
      if (ka.primary < kb.primary) return -1;
      if (ka.primary > kb.primary) return 1;
      if (ka.secondary < kb.secondary) return -1;
      if (ka.secondary > kb.secondary) return 1;
      return 0;
    });
    return mapped;
  }, [
    crossLinks,
    localOverrides,
    input,
    thisInputDisplayName,
    thisInputFallback,
  ]);

  // Lista pos-filtro (texto digitado pelo usuario). O filtro casa
  // contra: nome do input atual + nome de cada participante + meta_name
  // + colunas declaradas (keys/measure). Acentos sao tolerados.
  const filteredList = useMemo(() => {
    const q = normalizeForSearch(nameFilter);
    if (!q) return enrichedList;
    const matches = (xl) => {
      const haystack = [];
      haystack.push(thisInputDisplayName);
      haystack.push(xl?.meta_name);
      // ``display_name`` do xl pode ser string ou dict localizado.
      const dn = xl?.display_name;
      if (dn && typeof dn === "object") {
        haystack.push(dn.pt, dn.es, dn.en);
      } else if (typeof dn === "string") {
        haystack.push(dn);
      }
      for (const p of xl?.other_participants || []) {
        haystack.push(p?.display_name, p?.input_meta_name);
      }
      for (const k of xl?.key_columns || []) {
        haystack.push(k);
      }
      if (xl?.measure_column) haystack.push(xl.measure_column);
      return haystack.some((s) => normalizeForSearch(s).includes(q));
    };
    return enrichedList.filter(({ xl }) => matches(xl));
  }, [enrichedList, nameFilter, thisInputDisplayName]);

  if (loading) {
    return (
      <DrawerPaneLoadingLayout
        variant="input"
        scrollRef={wheelRef}
        overlayScrollClass={overlayScrollClasses.overlayScroll}
        title={t("modeling_inputs_v3.cross_links.loading_title")}
        message={t("modeling_inputs_v3.cross_links.loading")}
      />
    );
  }

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      <div className={classes.section}>
        <div className={classes.xlinkExplainer}>
          <InfoOutlined className={classes.xlinkExplainerIcon} />
          <div>
            {t("modeling_inputs_v3.cross_links.explainer_intro")}
            <ul className={classes.xlinkExplainerList}>
              <li>
                <strong>
                  {t("modeling_inputs_v3.cross_links.explainer_warn_limit")}
                </strong>{" "}
                — {t("modeling_inputs_v3.cross_links.explainer_warn_desc")}
              </li>
              <li>
                <strong>
                  {t("modeling_inputs_v3.cross_links.explainer_crit_limit")}
                </strong>{" "}
                — {t("modeling_inputs_v3.cross_links.explainer_crit_desc")}
              </li>
            </ul>
          </div>
        </div>
        {/* Onda 3.4 (CCU Testes, mai/2026): caixa explainer dedicada
            ao conceito de "input autoritativo". Antes este texto vivia
            dentro do ThresholdEditor de CADA cruzamento — repetia o
            mesmo paragrafo ate 39 vezes no scroll. Movido para o topo
            como caixa informativa unica, igual padrao do explainer dos
            limites. */}
        <div className={classes.xlinkExplainer}>
          <InfoOutlined className={classes.xlinkExplainerIcon} />
          <div>
            <strong>
              {t(
                "modeling_inputs_v3.cross_links.explainer_authoritative_title",
              )}
            </strong>{" "}
            — {t("modeling_inputs_v3.cross_links.explainer_authoritative_desc")}
          </div>
        </div>

        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.cross_links.section_title")}
          <span className={classes.sectionCount}>{crossLinks.length}</span>
        </div>

        {/* Filtro de texto (2026-05-27). Mesmo formato/estilo do filtro
            de inputs da view principal (``InputsV3.js``), incluindo as
            camadas anti-autofill: honeypot ``username`` invisivel,
            ``readOnly`` ate primeiro focus, ``autoComplete`` aleatorio
            e ``data-*`` ignores para 1Password/LastPass/Chrome. */}
        {crossLinks.length > 0 && (
          <div style={{ margin: "4px 0 10px 0", maxWidth: 320 }}>
            <input
              type="text"
              name="username"
              autoComplete="username"
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: -10000,
                top: -10000,
                width: 1,
                height: 1,
                opacity: 0,
              }}
              readOnly
            />
            <TextField
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder={t(
                "modeling_inputs_v3.cross_links.filter_placeholder",
                "Buscar cruzamento",
              )}
              variant="standard"
              size="small"
              fullWidth
              autoComplete="off"
              name={filterFieldName}
              onFocus={(e) => e.target.removeAttribute("readonly")}
              inputProps={{
                readOnly: true,
                autoComplete: "new-password",
                "data-lpignore": "true",
                "data-form-type": "other",
                "data-1p-ignore": "true",
                role: "presentation",
                spellCheck: "false",
                autoCorrect: "off",
                autoCapitalize: "off",
                style: {
                  padding: 0,
                  height: 24,
                  lineHeight: "24px",
                },
              }}
              InputProps={{
                disableUnderline: true,
                style: {
                  background: "#f1f3f4",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 10px",
                  minHeight: 32,
                  fontSize: "0.85rem",
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" style={{ color: grayColor[1] }} />
                  </InputAdornment>
                ),
                endAdornment: nameFilter ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t(
                        "inputs_v3.controls.clear_search",
                        "Limpar busca",
                      )}
                      onClick={() => setNameFilter("")}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </div>
        )}

        {error && (
          <PaneErrorBanner
            title={t("modeling_inputs_v3.cross_links.error_title")}
            message={t("modeling_inputs_v3.cross_links.error_message")}
            detail={error}
            onRetry={() => {
              if (loading || typeof drawerDetails?.refresh !== "function") {
                return;
              }
              drawerDetails.refresh();
            }}
            retrying={loading}
          />
        )}

        {!error && crossLinks.length === 0 && (
          <div
            className={classes.muted}
            style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
          >
            <CompareArrows style={{ flex: "0 0 auto", marginTop: 2 }} />
            <span>{t("modeling_inputs_v3.cross_links.no_cross_links")}</span>
          </div>
        )}

        {!error && crossLinks.length > 0 && filteredList.length === 0 && (
          <div
            className={classes.muted}
            style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
          >
            <Search style={{ flex: "0 0 auto", marginTop: 2 }} />
            <span>
              {t(
                "modeling_inputs_v3.cross_links.filter_no_results",
                "Nenhum cruzamento encontrado para o filtro atual.",
              )}
            </span>
          </div>
        )}

        {!error &&
          filteredList.map(({ xl, warnPct, critPct }) => {
            const chain = buildChain(
              xl,
              input?.meta_name,
              thisInputDisplayName,
              thisInputFallback,
            );
            const subtitle = buildSubtitle(xl, t);
            const effectivePeriodText = buildEffectivePeriod(xl, t, lang);
            const isOpen = expandedId === xl.meta_name;
            const draft = drafts[xl.meta_name];
            const historyOpen = openHistoryId === xl.meta_name;
            const initialAuthorities = resolveAuthoritativeMetas(xl);
            const draftAuthorities = draft?.authoritativeMetaNames || [];
            const authoritiesChanged =
              initialAuthorities.length !== draftAuthorities.length ||
              initialAuthorities.some((m) => !draftAuthorities.includes(m)) ||
              draftAuthorities.some((m) => !initialAuthorities.includes(m));
            const onCriticalChanged =
              (draft?.onCriticalSeverity || "ask") !==
              (xl.on_critical_severity || "ask");
            const changed =
              isOpen &&
              draft &&
              (draft.warnPct !== warnPct ||
                draft.critPct !== critPct ||
                authoritiesChanged ||
                onCriticalChanged);

            return (
              <div key={xl.meta_name} className={classes.xlinkCard}>
                <div className={classes.xlinkChain}>
                  {chain.map((node, i) => (
                    <React.Fragment key={i}>
                      {i > 0 ? (
                        <span className={classes.xlinkChainArrow}>↔</span>
                      ) : null}
                      <span
                        className={
                          node.isEndpoint
                            ? classes.xlinkChainEndpoint
                            : classes.xlinkChainIntermediate
                        }
                        style={
                          node.isThis
                            ? {
                                textDecoration: "underline",
                                textUnderlineOffset: 2,
                              }
                            : undefined
                        }
                        title={node.isThis ? thisInputFallback : undefined}
                      >
                        {node.label}
                      </span>
                    </React.Fragment>
                  ))}
                </div>

                {subtitle ? (
                  <div className={classes.xlinkSubtitle}>{subtitle}</div>
                ) : null}

                {effectivePeriodText ? (
                  <div className={classes.xlinkSubtitle}>
                    {effectivePeriodText}
                  </div>
                ) : null}

                <div className={classes.xlinkFooter}>
                  <span className={classes.xlinkSummary}>
                    {warnPct !== null ? (
                      <>
                        <span className={classes.xlinkSummaryWarn}>
                          {t("modeling_inputs_v3.cross_links.attention_below", {
                            pct: formatCoverageLimit(warnPct, naLabel),
                          })}
                        </span>
                        {" · "}
                        <span className={classes.xlinkSummaryErr}>
                          {t("modeling_inputs_v3.cross_links.critical_below", {
                            pct: formatCoverageLimit(critPct, naLabel),
                          })}
                        </span>
                      </>
                    ) : (
                      <span className={classes.xlinkSummaryMuted}>
                        {t("modeling_inputs_v3.cross_links.limits_undefined")}
                      </span>
                    )}
                  </span>
                  {canEditThresholds && (
                    <button
                      type="button"
                      className={classes.xlinkEditToggle}
                      onClick={() => {
                        if (isOpen) {
                          closeEditor();
                        } else {
                          openEditor(xl.meta_name, {
                            warnPct,
                            critPct,
                            onCriticalSeverity:
                              xl.on_critical_severity || "ask",
                            authoritativeMetaNames:
                              resolveAuthoritativeMetas(xl),
                          });
                        }
                      }}
                    >
                      {isOpen
                        ? t("modeling_inputs_v3.cross_links.close_editor")
                        : t("modeling_inputs_v3.cross_links.edit_thresholds")}
                    </button>
                  )}
                </div>
                <div className={classes.xlinkEvaluationRow}>
                  <span className={classes.xlinkEvaluationText}>
                    {evaluationText(xl.evaluation, t)}
                  </span>
                  {(() => {
                    const tone = evaluationTone(xl.evaluation);
                    if (!tone) return null;
                    const toneClass =
                      tone === "success"
                        ? classes.xlinkEvaluationDotSuccess
                        : tone === "warning"
                          ? classes.xlinkEvaluationDotWarning
                          : classes.xlinkEvaluationDotDanger;
                    return (
                      <span
                        className={`${classes.xlinkEvaluationDot} ${toneClass}`}
                        aria-hidden="true"
                      />
                    );
                  })()}
                  <button
                    type="button"
                    className={`${classes.xlinkEditToggle} ${
                      historyOpen ? classes.xlinkEditToggleOpen : ""
                    }`}
                    onClick={() =>
                      setOpenHistoryId((prev) =>
                        prev === xl.meta_name ? null : xl.meta_name,
                      )
                    }
                  >
                    {t("modeling_inputs_v3.cross_links.history_button")}
                  </button>
                </div>

                {historyOpen ? (
                  <CoverageHistoryTable
                    classes={classes}
                    history={xl.history}
                    currentYear={year}
                    currentMonth={month}
                    t={t}
                  />
                ) : null}

                {isOpen && draft && canEditThresholds ? (
                  <ThresholdEditor
                    classes={classes}
                    draft={draft}
                    onDraftChange={(d) => updateDraft(xl.meta_name, d)}
                    onCancel={closeEditor}
                    onConfirm={() => confirmDraft(xl.meta_name)}
                    changed={Boolean(changed)}
                    saving={savingId === xl.meta_name}
                    errorMsg={savingId === null && saveError ? saveError : null}
                    xl={xl}
                    thisInputMeta={input?.meta_name}
                    thisInputDisplayName={thisInputDisplayName}
                    t={t}
                  />
                ) : null}
              </div>
            );
          })}
      </div>
    </div>
  );
};

CrossLinksPane.propTypes = {
  input: PropTypes.object,
  year: PropTypes.number,
  month: PropTypes.number,
  userRole: PropTypes.string,
  isUploading: PropTypes.bool,
  drawerDetails: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.any,
    crossLinks: PropTypes.array,
    refresh: PropTypes.func,
  }),
};

export default CrossLinksPane;
